/* eslint-disable @typescript-eslint/no-explicit-any */
import React from "react";
import { Form } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import Button from "components/base/Button";
import PhoenixModal from "../components/PhoenixModal";
import { UilCheck } from "@iconscout/react-unicons";

import { TreeNodeRoute, TreeNodeType } from "../components/InspectionRouteTree/types/treeNode";
import { EquipmentInspectionStatus, EquipmentInspectionStatusLabel } from "./utils/enumEquipmentInspectionStatus";
import ConditionTag from "../components/ConditionTag/ConditionTag";
import { ConditionType } from "../model/enumerations/condition-type.model";
import { allEquipmentsInspected, collectEquipmentsToUpdate, markAllEquipmentsAsInspected } from "./utils/treeUtils";
import { findEquipmentInGroups } from "../InspectionRoute/utils/functionsTreeNode";

import FeatherIcon from 'feather-icons-react';

type Props = {
    node: TreeNodeRoute;
    update: (id: string, updater: (n: TreeNodeRoute) => TreeNodeRoute) => void;
    isViewMode?: boolean;
    onStatusChange?: (id: string, status: EquipmentInspectionStatus) => void;
    onFinishGroup?: (id: string) => void;
};

export function NodeExtras({
    node,
    update,
    isViewMode = false,
    onStatusChange,
    onFinishGroup,
}: Props) {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const tt = (key: string, options?: Record<string, unknown>) => t(key, { ns: "translation", ...options });

    const recordData = useSelector((s: any) => s.inspectionRecord?.data);
    const groups = useSelector((s: any) => s.inspectionRecord?.groups ?? []);
    const plantId = recordData?.plant?.id ?? "";
    const routeId = recordData?.id ?? "";

    const groupEquipment = React.useMemo(() => {
        if (node.type !== TreeNodeType.ELEMENT) return null;
        return findEquipmentInGroups(groups, node.id);
    }, [groups, node.id, node.type]);

    const equipmentId = groupEquipment?.equipment?.id ?? "";


    const defaultStatus = node.status ?? EquipmentInspectionStatus.NOT_INSPECTED;

    // ---------------- GROUP ----------------
    const [showModal, setShowModal] = React.useState(false);

    if (node.type === TreeNodeType.GROUP) {
        const isDone = allEquipmentsInspected(node);

        if (isDone) {
            return (
                <span
                    style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 6,
                        padding: "0px 8px",
                        marginTop: 3,
                        marginBottom: 3,
                        borderRadius: 6,
                        background: "#d1e7dd",
                        color: "#0f5132",
                        fontSize: 12,
                        fontWeight: 600,
                        border: "1px solid #badbcc",
                    }}
                >
                    {tt("pages.thermography.inspectionRecord.nodeExtras.inspectionDone")}
                    <UilCheck size={16} color="#0f5132" />
                </span>
            );
        }

        const handleFinishClick = () => {
            const equipmentsToUpdate = collectEquipmentsToUpdate(node);
            update(node.id, n => markAllEquipmentsAsInspected(n));
            equipmentsToUpdate.forEach(({ id, status }) => {
                onStatusChange?.(id, status);
            });
            onFinishGroup?.(node.id);
            setShowModal(false);
        };

        return (
            <>
                {!isViewMode && (
                    <Button variant="outline-info" disabled={isViewMode} onClick={() => setShowModal(true)}
                        style={{ padding: 4, marginTop: 3, marginBottom: 3, fontSize: 12 }}>
                        {tt("pages.thermography.inspectionRecord.nodeExtras.finishInspection")}
                    </Button>
                )}

                <PhoenixModal
                    show={showModal}
                    onHide={() => setShowModal(false)}
                    title={tt("pages.thermography.inspectionRecord.nodeExtras.finishInspectionTitle")}
                    size="fullscreen-sm-down"
                    backdrop="static"
                    footer={
                        <>
                            <Button variant="phoenix-secondary" onClick={() => setShowModal(false)}>
                                {tt("pages.thermography.inspectionRecord.nodeExtras.cancel")}
                            </Button>
                            <Button variant="primary" onClick={handleFinishClick}>
                                {tt("pages.thermography.inspectionRecord.nodeExtras.confirm")}
                            </Button>
                        </>
                    }
                >
                    {tt("pages.thermography.inspectionRecord.nodeExtras.finishConfirmation")}{" "}
                    <strong>{node.title ?? "--"}</strong>?
                </PhoenixModal>
            </>
        );
    }

    // ---------------- ELEMENT (Equipment) ----------------
    if (node.type === TreeNodeType.ELEMENT) {
        const canCreate =
            !isViewMode &&
            (node.status === EquipmentInspectionStatus.NOT_INSPECTED || node.status == null);

        return (
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                {canCreate && (
                    <Button
                        style={{ padding: 4, marginTop: 3, marginBottom: 3, fontSize: 12 }}
                        variant="phoenix-secondary"
                        onClick={() => {
                            if (!plantId || !equipmentId || !routeId) return;
                            navigate(`/thermalAnomalyForm/new_trr/${plantId}/${equipmentId}/${routeId}`);
                        }}
                    >
                        {tt("pages.thermography.inspectionRecord.nodeExtras.createAnomaly")}
                    </Button>
                )}

                <Form.Select
                    size="sm"
                    style={{ paddingBottom: 1, paddingTop: 1, marginTop: 3, marginBottom: 3 }}
                    value={defaultStatus}
                    disabled={isViewMode}
                    onChange={(e) => {
                        const newStatus = e.target.value as EquipmentInspectionStatus;
                        update(node.id, (n) => ({ ...n, status: newStatus }));
                        onStatusChange?.(node.id, newStatus);
                    }}
                >
                    {Object.values(EquipmentInspectionStatus).map((st) => (
                        <option key={st} value={st}>
                            {EquipmentInspectionStatusLabel[st]}
                        </option>
                    ))}
                </Form.Select>
            </div>
        );
    }

    // ---------------- ITEM ----------------
    if (node.type === TreeNodeType.ITEM) {
        const condition = (node as any).condition as ConditionType | undefined;
        if (!condition) return null;

        return (
            <div style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                <ConditionTag condition={condition} />

                {!isViewMode && (
                    <Button
                        style={{ padding: 4, marginTop: 3, marginBottom: 3 }}
                        onClick={() => navigate(`/thermalAnomalyForm/edit_trr/${node.id}`)}
                        className="icon-button edit-button"
                        aria-label={tt("pages.thermography.inspectionRecord.nodeExtras.edit")}
                        title={tt("pages.thermography.inspectionRecord.nodeExtras.editAnomaly")}
                    >
                        <FeatherIcon icon="edit-2" size={16} />
                    </Button>
                )}
            </div>
        );
    }

}
